<template>
  <!-- La feuille du code parent (HARMONIA-87). Une seule, montée dans App.vue,
       qui répond à l'entrée en mode enfant (création du code) comme à sa sortie
       (vérification) : voir useParentCodeGate. Centrée plutôt qu'en bas : le
       pavé numérique du système monte et recouvrirait une feuille du bas. -->
  <AppSheet :is-open="gate.open" mode="centered" transparent :backdrop-dismiss="!busy" :aria-label="title"
    @update:is-open="onOpenChange" @did-present="focusCode">
    <div class="flex h-full items-center justify-center">
      <div class="mx-4 w-full max-w-sm rounded-[20px] border border-line bg-white px-6 py-7 shadow-2xl"
        data-testid="parent-code-sheet" :data-mode="gate.mode" :data-step="step">
        <template v-if="step !== 'forgot'">
          <div class="text-center">
            <h2 class="ob-display text-2xl leading-tight text-ink">{{ title }}</h2>
            <p class="mt-2 text-sm leading-[1.5] text-ink-3">{{ subtitle }}</p>
          </div>

          <!-- Les cases des codes côté parent (invitation, code reçu par
               email) : elles savent déjà prendre un code collé ou proposé par
               le clavier. -->
          <div role="group" class="mt-7" :aria-label="t('childMode.code.inputLabel')"
            data-testid="parent-code-input">
            <OtpInput ref="otpRef" v-model="code" :length="PARENT_CODE_LENGTH"
              :disabled="busy || lockedFor > 0" :invalid="invalid" @complete="onComplete" />
          </div>

          <p v-if="message" role="alert" class="mt-4 text-center text-sm"
            :class="lockedFor > 0 ? 'text-ink-3' : 'text-danger'" data-testid="parent-code-message">
            {{ message }}
          </p>

          <div class="mt-6 flex flex-col items-center gap-1">
            <button v-if="gate.mode === 'verify'" type="button" data-testid="parent-code-forgot"
              class="min-h-11 px-3 text-sm font-semibold text-ink-2 underline underline-offset-4"
              @click="step = 'forgot'">
              {{ t('childMode.code.forgot') }}
            </button>
            <Button color="light" color-variant="outline" expand="block" data-testid="parent-code-cancel"
              :disabled="busy" @click="settle('cancelled')">
              {{ t('common.cancel') }}
            </Button>
          </div>
        </template>

        <!-- « Code oublié ? » : se déconnecter est la seule sortie sans code, et
             un enfant ne peut pas se reconnecter ensuite. -->
        <template v-else>
          <div class="text-center">
            <h2 class="ob-display text-2xl leading-tight text-ink">{{ t('childMode.code.forgotTitle') }}</h2>
            <p class="mt-2 text-sm leading-[1.5] text-ink-2">{{ t('childMode.code.forgotBody') }}</p>
          </div>
          <div class="mt-6 flex flex-col gap-2">
            <Button expand="block" data-testid="parent-code-forgot-confirm" @click="settle('forgot')">
              {{ t('childMode.code.forgotConfirm') }}
            </Button>
            <Button color="light" color-variant="outline" expand="block" data-testid="parent-code-forgot-back"
              @click="backToCode">
              {{ t('common.cancel') }}
            </Button>
          </div>
        </template>
      </div>
    </div>
  </AppSheet>
</template>

<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AppSheet from '@/components/common/AppSheet.vue'
import OtpInput from '@/components/auth/OtpInput.vue'
import { Button } from '@/components/ui/button'
import {
  PARENT_CODE_LENGTH,
  parentCodeLockoutRemaining,
  setParentCode,
  verifyParentCode,
} from '@/services/parentCode.service'
import { settleParentCode, useParentCodeGateState, type ParentCodeResult } from '@/composables/useParentCodeGate'

const { t } = useI18n()
const gate = useParentCodeGateState()

/** `enter` puis `confirm` à la création ; `enter` seul à la vérification. */
type Step = 'enter' | 'confirm' | 'forgot'
const step = ref<Step>('enter')
const code = ref('')
const firstCode = ref('')
const message = ref('')
/** Cases en rouge après un refus, jusqu'à la frappe suivante. */
const invalid = ref(false)
const busy = ref(false)
const lockedFor = ref(0)
const otpRef = ref<InstanceType<typeof OtpInput> | null>(null)

let countdown: ReturnType<typeof setInterval> | null = null

const title = computed(() => {
  if (gate.mode === 'verify') return t('childMode.code.enterTitle')
  return step.value === 'confirm' ? t('childMode.code.confirmTitle') : t('childMode.code.createTitle')
})

const subtitle = computed(() => {
  if (gate.mode === 'verify') return t('childMode.code.enterSubtitle')
  return step.value === 'confirm' ? t('childMode.code.confirmSubtitle') : t('childMode.code.createSubtitle')
})

function focusCode() {
  void nextTick(() => otpRef.value?.focusFirst?.())
}

// La frappe suivante efface le rouge du refus.
watch(code, (value) => { if (value) invalid.value = false })

function stopCountdown() {
  if (countdown) clearInterval(countdown)
  countdown = null
}

// Au-delà d'une minute et demie, l'attente se dit en minutes : « 3600 s » ne
// se lit pas.
function lockoutMessage(seconds: number): string {
  return seconds > 90
    ? t('childMode.code.tooManyMinutes', { minutes: Math.ceil(seconds / 60) })
    : t('childMode.code.tooMany', { seconds })
}

function startCountdown(ms: number) {
  stopCountdown()
  lockedFor.value = Math.ceil(ms / 1000)
  message.value = lockoutMessage(lockedFor.value)
  countdown = setInterval(() => {
    lockedFor.value -= 1
    if (lockedFor.value <= 0) {
      stopCountdown()
      lockedFor.value = 0
      message.value = ''
      focusCode()
      return
    }
    message.value = lockoutMessage(lockedFor.value)
  }, 1000)
}

function fail(text: string) {
  message.value = text
  code.value = ''
  invalid.value = true
  otpRef.value?.shake?.()
  focusCode()
}

// Chaque demande repart d'un formulaire vide. Une vérification relit l'attente
// en cours : relancer la feuille ne doit pas remettre le compteur à zéro.
watch(() => gate.requestId, async () => {
  stopCountdown()
  step.value = 'enter'
  code.value = ''
  firstCode.value = ''
  message.value = ''
  invalid.value = false
  busy.value = false
  lockedFor.value = 0
  if (gate.mode === 'verify') {
    const remaining = await parentCodeLockoutRemaining()
    if (remaining > 0) startCountdown(remaining)
  }
})

async function onComplete(value: string) {
  if (busy.value || lockedFor.value > 0) return

  if (gate.mode === 'create') {
    if (step.value === 'enter') {
      firstCode.value = value
      code.value = ''
      message.value = ''
      step.value = 'confirm'
      focusCode()
      return
    }
    if (value !== firstCode.value) {
      firstCode.value = ''
      step.value = 'enter'
      fail(t('childMode.code.mismatch'))
      return
    }
    busy.value = true
    try {
      await setParentCode(value)
      settle('ok')
    } catch (error) {
      console.error('[ParentCodeSheet] setParentCode failed', error)
      busy.value = false
      fail(t('childMode.code.mismatch'))
    }
    return
  }

  busy.value = true
  const result = await verifyParentCode(value)
  busy.value = false
  if (result.status === 'ok') {
    settle('ok')
  } else if (result.status === 'locked') {
    code.value = ''
    startCountdown(result.retryInMs)
  } else {
    fail(t('childMode.code.wrong'))
  }
}

function backToCode() {
  step.value = 'enter'
  focusCode()
}

function settle(result: ParentCodeResult) {
  stopCountdown()
  settleParentCode(result)
}

// Fermée par le fond ou le geste : c'est une annulation.
function onOpenChange(open: boolean) {
  if (!open && gate.open) settle('cancelled')
}

onUnmounted(() => {
  stopCountdown()
})
</script>
